import { z } from "zod";

export const StayAiWebhookEventList = [
  "SUBSCRIPTION",
  "CANCELLATION",
  "FAILED_PAYMENT",
  "SUCCESSFUL_PAYMENT",
  "SUBSCRIPTION_UPDATE",
  "DATA_EXPORTED",
] as const;

export type StayAiWebhookEventType = (typeof StayAiWebhookEventList)[number];

export type StayAiWebhookType = {
  id: string;
  createdAt: string;
  updatedAt: string;
  accountId: string;
  appId: string;
  event: StayAiWebhookEventType;
  url: string;
};

export type StayAiSwapLineProductRequest = {
  subscriptionId: string;
  lineId: string;
  variantId: string;
  price: string;
  quantity: string;
  initiator: "MERCHANT" | "CUSTOMER";
  isDiscounted: boolean;
};

export const StayAiCreateWebhookRequestBodySchema = z.object({
  event: z.enum(StayAiWebhookEventList),
  url: z.string(),
});

export type StayAiCreateWebhookRequestBodyType = z.infer<
  typeof StayAiCreateWebhookRequestBodySchema
>;

export const StayAiGlobalSettingsSchema = z.object({
  singlePurchaseQuerySelectors: z.array(z.string()).default([]),
  singlePurchasePriceQuerySelectors: z.array(z.string()).default([]),
  subscribeAndSaveQuerySelectors: z.array(z.string()).default([]),
  subscribeAndSavePriceQuerySelectors: z.array(z.string()).default([]),
  subscribeAndSavePercentageQuerySelectors: z.array(z.string()).default([]),
  installmentPriceQuerySelectors: z.array(z.string()).default([]),
});

export type StayAiGlobalSettingsType = z.infer<
  typeof StayAiGlobalSettingsSchema
>;

export const StayAiExperienceSettingsSchema = z.object({
  subscriptionPlanGroupId: z.string(),
  isSubDefault: z.boolean().nullish(),
  variationSettings: z
    .object({
      variationId: z.string().uuid(),
      discount: z.number().nullish(),
      preSubscribeText: z.string().nullish(),
      postSubscribeText: z.string().nullish(),
      oneTimeMessage: z.string().nullish(),
    })
    .array()
    .nullish(),
});
export type StayAiExperienceSettingsType = z.infer<
  typeof StayAiExperienceSettingsSchema
>;

export interface StayAiSubscription {
  id: string;
  createdAt: string;
  updatedAt: string;
  shopifySubscriptionContractId: string;
  status: string;
  shopifyCreatedAt?: string;
  nextBillingDate: string;
  scheduledBillingDate?: string;
  pausedUntil: any;
  dunningStartedAt: any;
  dunningExitedAt: any;
  cancelledAt: any;
  cancellationReasons: any;
  cancelledBy: any;
  billingPolicy: BillingPolicy;
  anchors: any;
  assignedPricingPolicy: AssignedPricingPolicy[];
  address: Address;
  giftAddress: any;
  lineCount: number;
  originOrderId: string;
  originOrderPrice: number;
  currencyCode: string;
  failedPaymentBillingAttempts: number;
  outOfStockBillingAttempts: number;
  prePaid: boolean;
  prepaidNextDeliveryDate: any;
  prepaidShipmentsRemaining: number;
  prepaidDeliveryPolicy: any;
  prepaidAllowRenewal: boolean;
  prepaidRenewNextOrder: boolean;
  prepaidtotalShipmentsPerCycle: any;
  prepaidCancelAtEndOfCycle: boolean;
  paymentMethodId: string;
  deliveryPrice: number;
  deletedAt: any;
  lastSeenIp: string;
  churnRisk?: any;
  notes: any;
  accountId: string;
  accountCustomerId: string;
  lines: StayAiLine[];
}

export type StayAiLine = {
  id: string;
  createdAt: string;
  updatedAt: string;
  subscriptionContractLineItemId: string;
  shopifySellingPlanId: string;
  title: string;
  shopifyProductId: string;
  variantId: string;
  variantTitle: string;
  variantImage: string;
  sku: any;
  currentPrice: number;
  lineDiscountedPrice: number;
  quantity: number;
  isOneTime: boolean;
  outOfStockRollover: number;
  shouldSkipItem: boolean;
  bundleId: any;
  prepaidOriginalContractLinePrice: number;
  customAttributes: CustomAttribute[];
  type: string;
  subscriptionContractId: string;
  shopifySubscriptionContractPromotionId: any;
};

interface CustomAttribute {
  key: string;
  value: string;
}

interface BillingPolicy {
  interval: string;
  intervalCount: number;
}

type AssignedPricingPolicy =
  | {
      adjustmentType: "PERCENTAGE";
      adjustmentValue: AdjustmentValue;
    }
  | {
      adjustmentType: "FIXED_AMOUNT";
      adjustmentValue: {
        amount: string;
        currencyCode: string;
      };
    };

interface AdjustmentValue {
  percentage: number;
}

interface Address {
  zip: string;
  city: string;
  phone: any;
  company: any;
  country: string;
  address1: string;
  address2: any;
  lastName: string;
  province: string;
  firstName: string;
  countryCode: string;
  provinceCode: string;
}

interface Customer {
  id: string;
  createdAt: string;
  updatedAt: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  shopifyCustomerId: string;
}

interface ShopifyPayload {
  id: number;
  status: string;
  customer_id: number;
  revision_id: string;
  currency_code: string;
  billing_policy: BillingPolicy2;
  delivery_policy: DeliveryPolicy;
  origin_order_id: number;
  admin_graphql_api_id: string;
  admin_graphql_api_customer_id: string;
  admin_graphql_api_origin_order_id: string;
}

interface BillingPolicy2 {
  interval: string;
  max_cycles: any;
  min_cycles: any;
  interval_count: number;
}

interface DeliveryPolicy {
  interval: string;
  interval_count: number;
}
